/**
 * SLOC Updation: the rules the handheld and the API share.
 *
 * seedtrac's SLOCUpdation app (`SLOCUpdation-App-main/`, `docs/reference/seedtrac-apps-map.md` §4)
 * moves lots between SLOCs of one plant in two legs joined by a transaction:
 *
 *     Pick ──Add──▶ pending list (draft) ──Submit──▶ open transaction (previewed)
 *                                                      │ Place (partial, repeatable)
 *                                                      ▼
 *                                    nothing left ──Submit──▶ closed (submitted)
 *
 * Every rule here is enforced twice. The handheld runs it live, so the operator gets the legacy
 * toast the moment they tick a row. The API runs it again inside the write, against balances it has
 * just locked. The client's arithmetic is only for display: the server derives every balance
 * itself, which the legacy app did not.
 *
 * BAGS ARE NOT CONSERVED on a pick. The legacy row lets the operator type the Balance NoB, because
 * taking 20 kg out of a 30 kg bag leaves one bag behind AND carries one away. So a pick records three
 * numbers: the qty taken, the bags carried (Pick NoB), and the bags left in the bin (Balance NoB).
 * The bin loses `existing − balance` bags, and those are what the pick reserves. A place moves whole
 * carried bags. Only the last placement of a line may take the last of its qty, and it takes every
 * bag still carried.
 */
import { z } from "zod";
import { canonicalBinCode, normalizeBinCode } from "./bin-codes";
import { toDecimal, type Decimal, type DecimalInput } from "./decimal";

export const SLOC_UPDATION_FORM = "sloc_updation";

/** An amount of one lot: kg and a bag count. */
export interface SlocAmount {
  qty: DecimalInput;
  bags: number;
}

/** What the operator typed on a Pick row. */
export interface PickEntry extends SlocAmount {
  /** Bags left in the bin (Balance NoB). */
  balanceBags: number;
}

const QTY_DP = 3;
/** The wire shape allows 7 integer digits; the ledger column is numeric(14,3). */
const QTY_MAX = 9_999_999;

const dec = (v: DecimalInput): Decimal => toDecimal(v ?? 0);
const isWholeCount = (n: number) => Number.isInteger(n) && n >= 0;

/**
 * The Balance NoB the legacy row fills in by itself as the operator types. It is Existing − Pick,
 * then 1 while any qty stays behind (a part bag is still a bag), and 0 once nothing does.
 */
export function suggestBalanceBags(
  existing: SlocAmount,
  pick: SlocAmount
): number {
  const balQty = dec(existing.qty).minus(dec(pick.qty));
  if (balQty.lte(0)) return 0;
  const bal = existing.bags - (pick.bags || 0);
  return bal >= 1 ? bal : 1;
}

/**
 * The Balance NoB a partial take leaves. Kg left behind with 0 bags is still a bag: the ledger keeps
 * it as 1 (apps/api/src/lib/ledger.ts), and `added` says so for the "Added a new bag" note.
 */
export function keepOneBag(
  balanceBags: number,
  balanceQty: DecimalInput
): { bags: number; added: boolean } {
  const added = balanceBags <= 0 && dec(balanceQty).gt(0);
  return { bags: added ? 1 : balanceBags, added };
}

/** Crop Variety desk: take bags and actual weighed quantity are independent user inputs. */
export function deskPickBalance(
  existing: SlocAmount,
  take: SlocAmount
): { bags: number; qty: string } {
  return {
    bags: existing.bags - take.bags,
    qty: dec(existing.qty).minus(dec(take.qty)).toFixed(QTY_DP),
  };
}

export type SlocReportPeriod = "today" | "date" | "range" | "all";

/** Inclusive document dates, shared by the web and handheld completed-update reports. */
export function slocReportDates(
  mode: SlocReportPeriod,
  today: string,
  date: string,
  from: string,
  to: string
): { from?: string; to?: string; error?: string } {
  if (mode === "all") return {};
  if (mode === "today") return { from: today, to: today };
  if (mode === "date")
    return date ? { from: date, to: date } : { error: "Choose a report date." };
  if (!from || !to) return { error: "Choose both From and To dates." };
  if (from > to) return { error: "From date must not be after To date." };
  return { from, to };
}

/** Existing − Pick, never below zero. What the Balance block shows. */
export function pickBalance(
  existing: SlocAmount,
  entry: PickEntry
): { qty: string; bags: number } {
  const qty = dec(existing.qty).minus(dec(entry.qty));
  return {
    qty: (qty.isNegative() ? toDecimal(0) : qty).toFixed(QTY_DP),
    bags: entry.balanceBags,
  };
}

/** Bags that leave the bin on this pick: the bin's existing bags less those left behind. */
export const bagsOut = (existing: SlocAmount, entry: PickEntry): number =>
  Math.max(existing.bags - entry.balanceBags, 0);

function qtyError(qty: DecimalInput, max: DecimalInput): string | null {
  let q: Decimal;
  try {
    q = dec(qty);
  } catch {
    return "Selected item quantity is 0";
  }
  if (q.lte(0)) return "Selected item quantity is 0";
  if (q.decimalPlaces() > QTY_DP) return "Qty can have at most 3 decimals";
  if (q.gt(QTY_MAX)) return "Qty is too large";
  if (q.gt(dec(max))) return "Qty Can't be greater than Ext. Qty";
  return null;
}

/**
 * The first thing wrong with a Pick row, in the legacy Preview's order, or null. `existing` is what
 * the operator may still take from this SLOC: the bin's balance less what other picks hold.
 */
export function pickRowError(
  existing: SlocAmount,
  entry: PickEntry
): string | null {
  const q = qtyError(entry.qty, existing.qty);
  if (q) return q;
  if (!isWholeCount(entry.bags) || entry.bags < 1)
    return "Selected item NoB is 0";
  if (!isWholeCount(entry.balanceBags))
    return "Balance NoB must be a whole number";
  if (entry.balanceBags > existing.bags)
    return "Balance NoB can't be greater than Ext. NoB";
  const balQty = dec(existing.qty).minus(dec(entry.qty));
  if (balQty.gt(0) && entry.balanceBags < 1) return "Selected item Balnob is 0";
  if (balQty.lte(0) && entry.balanceBags !== 0)
    return "Balance NoB must be 0 when the whole qty is picked";
  return null;
}

/** What a picked line still has to place. */
export interface LineRemaining {
  qty: DecimalInput;
  bags: number;
}

/** Remaining − Place, never below zero. What a Place row's Balance block shows. */
export function placeBalance(
  remaining: LineRemaining,
  place: SlocAmount
): { qty: string; bags: number } {
  const qty = dec(remaining.qty).minus(dec(place.qty));
  return {
    qty: (qty.isNegative() ? toDecimal(0) : qty).toFixed(QTY_DP),
    bags: Math.max(remaining.bags - (place.bags || 0), 0),
  };
}

/** The first thing wrong with a Place row, or null. */
export function placeRowError(
  remaining: LineRemaining,
  place: SlocAmount
): string | null {
  const q = qtyError(place.qty, remaining.qty);
  if (q) return q;
  if (!isWholeCount(place.bags) || place.bags < 1)
    return "Selected item NoB is 0";
  if (place.bags > remaining.bags) return "NoB can't be greater than Ext. NoB";
  const last = dec(place.qty).eq(dec(remaining.qty));
  if (last && place.bags !== remaining.bags)
    return `Place all ${remaining.bags} bags with the last of the qty`;
  if (!last && remaining.bags - place.bags < 1)
    return "Selected item Balnob is 0";
  return null;
}

/**
 * One picked line placed into several sub-bins at once, as Crop Variety wise moves a lot (user,
 * 2026-10-14). Bags belong to a destination as a whole, so each row takes any whole NoB of at
 * least one (a re-bagged lot may land as more or fewer sacks); only the kg must add up, every kg
 * at once. The first thing wrong, or null.
 */
export function pooledPlaceError(
  remaining: LineRemaining,
  splits: readonly SlocAmount[]
): string | null {
  if (splits.length === 0) return "Choose where it goes";
  for (const sp of splits) {
    const q = qtyError(sp.qty, QTY_MAX);
    if (q) return q;
    if (!isWholeCount(sp.bags) || sp.bags < 1) return "Selected item NoB is 0";
    if (sp.bags > 99_999) return "NoB is too large";
  }
  return placedQtyLeft(remaining, splits).isZero()
    ? null
    : "Source and destination quantities must have the same total.";
}

/** The kg still to place after these rows: Crop Variety wise's Balance Qty. Negative when over. */
export function placedQtyLeft(
  remaining: LineRemaining,
  splits: readonly { qty: DecimalInput }[]
): Decimal {
  let left = dec(remaining.qty);
  for (const sp of splits) {
    try {
      left = left.minus(dec(sp.qty || 0));
    } catch {
      /* the row's own error says so */
    }
  }
  return left;
}

/**
 * The first thing wrong with a Merge at a new SLOC, or null. `bagsNow` is the lot's bags there once
 * the move lands (those already there + those carried in); a merge pours sacks together, so the kg
 * stays and the count drops to `bagsAfter`, never to zero and never up. Stale counts, reservations
 * and an empty SLOC are the API's to refuse: only it sees them.
 */
export function mergeError(bagsNow: number, bagsAfter: number): string | null {
  if (!isWholeCount(bagsAfter) || bagsAfter < 1)
    return "NoB after move must be at least 1";
  if (bagsAfter >= bagsNow)
    return `NoB after move must be less than ${bagsNow} to merge`;
  return null;
}

/**
 * How many of the bags a pick reserved at its source bin have been released after `placedBags` were
 * placed. Once the line is fully placed, every reserved bag is released. Until then, one reserved bag
 * is released per bag placed, never more than were reserved. The API releases the difference on each
 * placement, so the reservation always reaches exactly zero.
 */
export function releasedSourceBags(
  reservedBags: number,
  placedBags: number,
  fullyPlaced: boolean
): number {
  return fullyPlaced ? reservedBags : Math.min(placedBags, reservedBags);
}

// ---------------------------------------------------------------------------------------------------
// Selection rules
// ---------------------------------------------------------------------------------------------------

/** What a row is, for the rules that compare rows. */
export interface SlocMaterial {
  stageId: number | null;
  stage: string;
  cropId: number | null;
  crop: string;
  /** Variety id, `coded:<spM>/<spF>` for a blind-coded lot, or `none`. */
  varietyKey: string;
  variety: string;
}

/** Crops that may never be picked together, whatever their stage (legacy adapters). */
export const SIMILAR_SEED_CROPS = ["Tomato", "Brinjal", "Chilli"] as const;

function similarSeedOf(crop: string): string | null {
  const c = crop.trim().toLowerCase();
  if (c === "chili") return "Chilli";
  return SIMILAR_SEED_CROPS.find((x) => x.toLowerCase() === c) ?? null;
}

const describe = (m: SlocMaterial) =>
  [m.stage, m.crop, m.variety].filter(Boolean).join(", ");

/**
 * Whether `adding` may join a pick that already holds `held`: the rows ticked on this screen plus
 * the operator's pending list. Returns the legacy toast, or null.
 */
export function pickSelectionError(
  held: readonly SlocMaterial[],
  adding: SlocMaterial
): string | null {
  if (held.some((h) => h.stageId !== adding.stageId)) return "Stage mismatch!";
  return similarSeedError(held, adding);
}

/** Tomato, Brinjal and Chilli never travel or sit together: the legacy toast, or null. */
export function similarSeedError(
  held: readonly SlocMaterial[],
  adding: SlocMaterial
): string | null {
  const mine = similarSeedOf(adding.crop);
  if (!mine) return null;
  const other = held.some((h) => {
    const theirs = similarSeedOf(h.crop);
    return theirs !== null && theirs !== mine;
  });
  return other ? `${mine} cannot be picked with similar seeds!` : null;
}

/** The pick rule over a whole list, row by row: what the API checks on Add and Submit. */
export function pickSelectionErrors(
  rows: readonly SlocMaterial[]
): string | null {
  for (let i = 1; i < rows.length; i++) {
    const e = pickSelectionError(rows.slice(0, i), rows[i]!);
    if (e) return e;
  }
  return null;
}

/**
 * The varietyKey the placement rules compare: the variety id, or — for a blind-coded lot — its
 * SP pair, so two coded lots with different parents never count as the same variety.
 */
export const varietyKeyOf = (l: {
  varietyId: number | null;
  spMale: string | null;
  spFemale: string | null;
}) =>
  l.varietyId !== null
    ? String(l.varietyId)
    : l.spMale || l.spFemale
      ? `coded:${l.spMale ?? ""}/${l.spFemale ?? ""}`
      : "none";

/**
 * Why `other` may not share a sub-bin with `adding`, or null. The same in every warehouse, cold
 * store included (user, 2026-10-10): a sub-bin keeps one stage, and one variety per crop; different
 * crops may share it.
 */
export function subBinClash(
  other: Pick<SlocMaterial, "stageId" | "cropId" | "varietyKey">,
  adding: Pick<SlocMaterial, "stageId" | "cropId" | "varietyKey">
): "stage" | "variety" | null {
  // Unknown is not conflicting: a lot with no stage, crop or variety yet clashes with nothing.
  const known = (v: unknown) => v !== null && v !== "none";
  if (
    known(other.stageId) &&
    known(adding.stageId) &&
    other.stageId !== adding.stageId
  )
    return "stage";
  if (
    known(other.cropId) &&
    other.cropId === adding.cropId &&
    known(other.varietyKey) &&
    known(adding.varietyKey) &&
    other.varietyKey !== adding.varietyKey
  )
    return "variety";
  return null;
}

/**
 * Whether `adding` may be placed into a sub-bin with the rows already ticked for it (`selected`)
 * and what already sits there (`occupants`). See `subBinClash`.
 */
export function placeCompatibilityError(
  selected: readonly SlocMaterial[],
  adding: SlocMaterial,
  occupants: readonly SlocMaterial[] = []
): string | null {
  for (const m of selected) {
    const c = subBinClash(m, adding);
    if (c === "stage")
      return `Cannot place! Stage mismatch as you've selected: ${describe(m)}`;
    if (c === "variety")
      return `Cannot place! Another ${m.crop} variety as you've selected: ${describe(m)}`;
  }
  for (const m of occupants) {
    const c = subBinClash(m, adding);
    if (c === "stage")
      return `Cannot place! This sub-bin holds ${m.stage} stock only: ${describe(m)}`;
    if (c === "variety")
      return `Cannot place! This sub-bin already holds another ${m.crop} variety: ${describe(m)}`;
  }
  return null;
}

// ---------------------------------------------------------------------------------------------------
// Transactions
// ---------------------------------------------------------------------------------------------------

/** Place SUBMIT closes a transaction only once nothing is left to place. */
export const canClose = (lines: readonly { remainingQty: DecimalInput }[]) =>
  lines.every((l) => dec(l.remainingQty).lte(0));

const collator = new Intl.Collator("en", { numeric: true });

/**
 * Crop & Variety search with one warehouse chosen: one row per bin, listing its sub-bins
 * ("Bin: B05 / Subbin: 17, 18"). Bins and sub-bins sort numerically, as the legacy report did.
 */
export function groupStockByBin(
  rows: readonly { bin: string; subBin: string | null }[]
): { bin: string; subBins: string[] }[] {
  const byBin = new Map<string, Set<string>>();
  for (const r of rows) {
    const set = byBin.get(r.bin) ?? new Set<string>();
    if (r.subBin) set.add(r.subBin);
    byBin.set(r.bin, set);
  }
  return [...byBin.entries()]
    .sort(([a], [b]) => collator.compare(a, b))
    .map(([bin, subs]) => ({
      bin,
      subBins: [...subs].sort((a, b) => collator.compare(a, b)),
    }));
}

// ---------------------------------------------------------------------------------------------------
// Codes
// ---------------------------------------------------------------------------------------------------

export interface SlocCode {
  wh: string;
  bin: string;
  subBin: string | null;
}

/**
 * A SLOC label as the floor's QR codes and the hardware scanner give it: `CS-01/K01`, optionally
 * with a sub-bin (`WH-01/B24/1`). Returns the legacy scanner's error text when it isn't one.
 */
export function parseSlocCode(
  raw: string | null | undefined
): { ok: true; code: SlocCode } | { ok: false; error: string } {
  const text = normalizeBinCode(raw).replace(/\s*\/\s*/g, "/");
  if (!text.includes("/")) return { ok: false, error: "Invalid SLOC" };
  const [wh, bin, subBin, ...rest] = text.split("/");
  if (!wh || !bin || rest.length) return { ok: false, error: "Invalid SLOC" };
  if (!wh.includes("-")) return { ok: false, error: "Wrong QR Code" };
  return { ok: true, code: { wh, bin, subBin: subBin || null } };
}

/**
 * Two codes name the same location. Codes are stored in one canonical spelling, but prod sub-bins
 * are `1`..`20` while a label may say `01`, so both sides are canonicalised first.
 */
export const sameSlocCode = (a: string, b: string) =>
  canonicalBinCode(a) === canonicalBinCode(b);

/** `WH/BIN[/SUB]`, the way every SLOC screen prints a location. */
export const slocLabel = (c: SlocCode) =>
  [c.wh, c.bin, c.subBin].filter(Boolean).join("/");

/**
 * The lot search box: the lot number without its plant code, a letter and five digits (`Y25918` for
 * `DY25918/00000/00R`). The legacy note says "5 character"; it means the five digits.
 */
export const SHORT_LOT_RE = /^[A-Z]\d{5}$/i;
export const parseShortLotNo = (
  raw: string | null | undefined
): string | null => {
  const t = (raw ?? "").trim().toUpperCase();
  return SHORT_LOT_RE.test(t) ? t : null;
};

// ---------------------------------------------------------------------------------------------------
// Wire shapes
// ---------------------------------------------------------------------------------------------------

const qty = z
  .string()
  .regex(
    /^\d{1,7}(\.\d{1,3})?$/,
    "Qty must be a number with at most 3 decimals"
  );
const count = z.number().int().min(0).max(99_999);
const id = z.number().int().positive();
const expectedVersion = z.number().int().positive();

/** The handheld desk a SLOC screen works (RSW / CSW / PSW), the same values as the web's `$stage`. */
export const slocDeskParam = z.enum(["raw", "condition", "pack"]);

/** GET /sloc-updation/stock: at least one of location, lot or crop. */
export const slocStockQuery = z
  .object({
    facilityId: z.coerce.number().int().positive().optional(),
    /** A WH (every bin under it), a bin (all its sub-bins) or a leaf. */
    locationId: z.coerce.number().int().positive().optional(),
    lotId: z.coerce.number().int().positive().optional(),
    /** The short lot number, `Y25918`. */
    lotNo: z
      .string()
      .trim()
      .regex(SHORT_LOT_RE, "Invalid Lot number")
      .optional(),
    cropId: z.coerce.number().int().positive().optional(),
    /** A variety id, `coded` for blind-coded lots, or `none`. */
    varietyId: z
      .union([z.coerce.number().int().positive(), z.enum(["coded", "none"])])
      .optional(),
    stageId: z.coerce.number().int().positive().optional(),
    /** The desk asking: only that stage's lots. Older handhelds send none. */
    stage: slocDeskParam.optional(),
  })
  .refine(
    (q) =>
      q.locationId !== undefined ||
      q.lotId !== undefined ||
      q.lotNo !== undefined ||
      q.cropId !== undefined,
    { message: "Choose a SLOC, a lot or a crop." }
  );
export type SlocStockQuery = z.infer<typeof slocStockQuery>;

export const pickRow = z.object({
  lotId: id,
  /** The source leaf: a sub-bin, or a bin with no sub-bins. */
  locationId: id,
  qty,
  bags: count.min(1),
  balanceBags: count,
});
export type PickRow = z.infer<typeof pickRow>;

/** POST /sloc-updation/pending/lines: Preview's ADD. */
export const pickAddBody = z.object({
  facilityId: id.optional(),
  /** The pending list's version as read, or null when the operator has none yet. */
  expectedVersion: expectedVersion.nullable(),
  rows: z.array(pickRow).min(1).max(200),
  /** Updated handhelds request independent source subtraction; old APKs retain their contract. */
  balanceMode: z.literal("subtract").optional(),
  /** The desk picking: every lot must be of its stage. Older handhelds send none. */
  stage: slocDeskParam.optional(),
});
export type PickAddBody = z.infer<typeof pickAddBody>;

/** PATCH /sloc-updation/:id/lines/:lineId: the pending list's Edit item. */
export const pickEditBody = z.object({
  expectedVersion,
  qty,
  bags: count.min(1),
  balanceBags: count,
  balanceMode: z.literal("subtract").optional(),
});
export type PickEditBody = z.infer<typeof pickEditBody>;

export const placeRow = z.object({ lineId: id, qty, bags: count.min(1) });
export type PlaceRow = z.infer<typeof placeRow>;

/** POST /sloc-updation/:id/place: Preview's PLACE. */
export const placeBody = z.object({
  expectedVersion,
  /** The destination leaf. */
  destLocationId: id,
  rows: z.array(placeRow).min(1).max(200),
  /** One same-lot merge at the chosen destination; absence keeps carried bags separate. */
  merges: z
    .array(
      z.object({
        lotId: id,
        mergeScope: z.enum(["incoming", "all"]),
        finalBags: count.min(1),
        expected: z.object({ bags: count, qty }),
      })
    )
    .max(200)
    .optional(),
});
export type PlaceBody = z.infer<typeof placeBody>;

/**
 * POST /sloc-updation/:id/place-many: one PLACE into one sub-bin or several, all or nothing, by
 * Crop Variety wise's rules (`pooledPlaceError`): every line named is placed whole, bags kept
 * separate (no merge). Each placement is one destination; a line may appear under several.
 */
export const placeManyBody = z
  .object({
    expectedVersion,
    placements: z
      .array(placeBody.omit({ expectedVersion: true, merges: true }))
      .min(1)
      .max(20),
  })
  .refine(
    (b) =>
      new Set(b.placements.map((p) => p.destLocationId)).size ===
      b.placements.length,
    { message: "A sub-bin is in the placement twice.", path: ["placements"] }
  )
  .refine((b) => b.placements.reduce((n, p) => n + p.rows.length, 0) <= 200, {
    message: "Too many rows in one place.",
    path: ["placements"],
  });
export type PlaceManyBody = z.infer<typeof placeManyBody>;

/** POST /sloc-updation/:id/place-check: ticking a Place row (legacy sloctochk). */
export const placeCheckBody = z.object({
  destLocationId: id,
  lineIds: z.array(id).min(1).max(200),
});
export type PlaceCheckBody = z.infer<typeof placeCheckBody>;

export const slocVersionedBody = z.object({ expectedVersion });
export type SlocVersionedBody = z.infer<typeof slocVersionedBody>;

// ------------------------------------------------------------------ place merge (web + handheld)

/** A Place's choice for one lot already at the destination: merge its bags, or keep them apart. */
export interface PlaceMergeState {
  mode: "merge" | "keep";
  scope: "incoming" | "all";
  after: string;
}
export const DEFAULT_PLACE_MERGE: PlaceMergeState = {
  mode: "merge",
  scope: "incoming",
  after: "",
};

/** The bags the lot holds at the destination after this place, or why the choice is incomplete. */
export function placementMerge(
  existingBags: number,
  incomingBags: number,
  state: PlaceMergeState
): { finalBags: number | null; error: string | null } {
  if (state.mode === "keep")
    return { finalBags: existingBags + incomingBags, error: null };
  if (state.scope === "all" && existingBags < 1)
    return {
      finalBags: null,
      error: "There are no existing bags to merge with.",
    };
  if (state.after === "")
    return { finalBags: null, error: "Enter NoB after merge." };
  const after = Number(state.after);
  const max =
    state.scope === "incoming" ? incomingBags : existingBags + incomingBags;
  if (!Number.isInteger(after) || after < 1 || after > max)
    return {
      finalBags: null,
      error: `NoB after merge must be between 1 and ${max}.`,
    };
  return {
    finalBags: after + (state.scope === "incoming" ? existingBags : 0),
    error: null,
  };
}
